import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Stack,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../AuthContext';
import { logAnalyticsEvent } from '../../utils/analytics';
import { isIOSShell } from '../auth/AuthUtils';
import { createCheckoutSession, openStripeUrl } from '../../utils/subscription';
import {
  getMembershipPricing,
} from '../../utils/subscriptionPricing';

function getPlanTitle(plan) {
  if (plan.interval === 'year') return 'Annual Membership';
  if (plan.interval === 'month') return 'Monthly Membership';
  return plan.description || 'Membership';
}

function getTrialCopy(plan) {
  if (plan.interval === 'year' && plan.monthlyEquivalentLabel) {
    return `Only ${plan.monthlyEquivalentLabel}/month, billed annually after trial.`;
  }

  return `14-day free trial, then ${plan.amountLabel}/${plan.intervalLabel}.`;
}

function MembershipPricingCards({
  source = 'membership_pricing_cards',
  compact = false,
  title = 'Start your 14-day free trial',
  description,
  showHeader = true,
  successPath = '/profile?success=true',
  cancelPath = '/subscribe?canceled=true',
}) {
  const { currentUser, subscriptionStatus } = useAuth();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [loadingPricing, setLoadingPricing] = useState(true);
  const [checkoutPriceId, setCheckoutPriceId] = useState(null);
  const [error, setError] = useState('');
  const checkoutStartedRef = useRef(false);
  // iOS shell only: checkout happens in Safari (guideline 3.1.1 link-out) and
  // this page stays mounted, so the browser flow's Stripe success_url redirect
  // never happens here. Tracks an in-flight Safari checkout so we can emulate
  // that redirect when the subscription activates. Survives the button-state
  // reset below.
  const shellCheckoutLaunchedRef = useRef(false);

  // iOS shell: when the user comes back from Safari without completing
  // checkout, un-freeze the plan buttons (in the browser the page navigated
  // away, so this state never outlived a checkout).
  useEffect(() => {
    if (!isIOSShell()) return undefined;
    const resetOnReturn = () => {
      if (document.visibilityState !== 'visible') return;
      if (!checkoutStartedRef.current) return;
      checkoutStartedRef.current = false;
      setCheckoutPriceId(null);
    };
    document.addEventListener('visibilitychange', resetOnReturn);
    return () => document.removeEventListener('visibilitychange', resetOnReturn);
  }, []);

  // iOS shell: the moment the Firestore listener reports the subscription as
  // live, take the user where Stripe's success_url would have — otherwise a
  // paid user is left staring at the pricing cards.
  useEffect(() => {
    if (!shellCheckoutLaunchedRef.current) return;
    const status = subscriptionStatus?.status;
    if (status === 'active' || status === 'trialing') {
      shellCheckoutLaunchedRef.current = false;
      navigate(successPath);
    }
  }, [subscriptionStatus, navigate, successPath]);

  useEffect(() => {
    let isMounted = true;

    setLoadingPricing(true);
    getMembershipPricing()
      .then((result) => {
        if (!isMounted) return;
        setPricing(result);
        setError(result.plans.length ? '' : 'Membership plans are not available right now.');
      })
      .catch((pricingError) => {
        if (!isMounted) return;
        console.error('Error loading membership pricing:', {
          message: pricingError.message,
          code: pricingError.code,
          error: pricingError,
        });
        setError('Membership plans are not available right now. Please try again later.');
      })
      .finally(() => {
        if (isMounted) {
          setLoadingPricing(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const handlePlanSelect = async (plan) => {
    if (checkoutStartedRef.current) return;

    if (!currentUser) {
      navigate('/login');
      return;
    }

    checkoutStartedRef.current = true;
    setCheckoutPriceId(plan.priceId);
    setError('');

    try {
      logAnalyticsEvent('subscription_plan_selected', {
        source,
        price_id: plan.priceId,
        interval: plan.interval,
      });

      const checkoutUrl = await createCheckoutSession(currentUser.uid, plan.priceId, {
        successUrl: `${window.location.origin}${successPath}`,
        cancelUrl: `${window.location.origin}${cancelPath}`,
      });
      if (isIOSShell()) {
        shellCheckoutLaunchedRef.current = true;
      }
      openStripeUrl(checkoutUrl);
    } catch (checkoutError) {
      console.error('Error creating checkout session:', checkoutError);
      setError('Unable to start checkout. Please try again.');
      setCheckoutPriceId(null);
      checkoutStartedRef.current = false;
      logAnalyticsEvent('subscription_plan_checkout_error', {
        source,
        price_id: plan.priceId,
        interval: plan.interval,
        error: checkoutError.message,
      });
    }
  };

  const heading = pricing?.productName || title;
  const body = description || pricing?.productDescription || 'Choose the Membership plan that works best for you.';

  return (
    <Stack spacing={compact ? 2 : 3} sx={{ pb: { xs: 8, sm: 0 } }}>
      {showHeader && (
        <Box sx={{ textAlign: 'center' }}>
          <Typography
            variant={compact ? 'h6' : 'h4'}
            component="h2"
            sx={{ fontWeight: 700, mb: 1 }}
          >
            {heading}
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '42rem', mx: 'auto' }}>
            {body}
          </Typography>
        </Box>
      )}

      {error && (
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {loadingPricing ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' },
            gap: { xs: 2.5, md: 3 },
            alignItems: 'stretch',
            mx: 'auto',
            width: '100%',
            maxWidth: compact ? '49rem' : '59rem',
            px: { xs: 0.5, sm: 1 },
            py: 0.5,
          }}
        >
          {pricing?.plans.map((plan) => {
            const isAnnual = plan.interval === 'year';
            const isCheckingOut = checkoutPriceId === plan.priceId;
            const isSlidingAway = checkoutPriceId && !isCheckingOut;

            return (
              <Box
                key={plan.priceId}
                sx={(theme) => ({
                  width: '100%',
                  display: 'flex',
                  opacity: isSlidingAway ? 0 : 1,
                  transform: isSlidingAway
                    ? { xs: 'translateY(0.75rem)', md: 'translateX(1rem)' }
                    : isCheckingOut
                      ? 'scale(1.01)'
                      : 'none',
                  maxHeight: isSlidingAway ? { xs: 0, md: '40rem' } : '40rem',
                  maxWidth: isSlidingAway ? { xs: '100%', md: 0 } : '100%',
                  overflow: isSlidingAway ? 'hidden' : 'visible',
                  pointerEvents: isSlidingAway ? 'none' : undefined,
                  transition: theme.transitions.create(
                    ['opacity', 'transform', 'max-height', 'max-width'],
                    {
                      duration: theme.transitions.duration.standard,
                      easing: theme.transitions.easing.easeInOut,
                    }
                  ),
                })}
              >
                <Card
                  variant="outlined"
                  sx={(theme) => ({
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    borderRadius: 3,
                    borderColor: isAnnual ? theme.palette.primary.main : theme.palette.divider,
                    borderWidth: isAnnual ? '0.125rem' : '0.0625rem',
                    backgroundColor: theme.palette.background.paper,
                    boxShadow: isAnnual ? theme.shadows[3] : theme.shadows[1],
                    transition: theme.transitions.create(['box-shadow', 'transform', 'border-color'], {
                      duration: theme.transitions.duration.short,
                    }),
                    '&:hover': checkoutPriceId ? {} : {
                      boxShadow: isAnnual ? theme.shadows[5] : theme.shadows[3],
                      transform: 'translateY(-0.125rem)',
                      borderColor: isAnnual ? theme.palette.primary.main : theme.palette.primary.light,
                    },
                  })}
                >
                  <CardContent sx={{ p: compact ? 2 : 3, width: '100%' }}>
                    <Stack spacing={2} sx={{ height: '100%' }}>
                      <Box>
                        {plan.displayLabel && (
                          <Typography
                            variant="overline"
                            sx={(theme) => ({
                              color: theme.palette.primary.main,
                              fontWeight: 700,
                              letterSpacing: '0.08em',
                            })}
                          >
                            {plan.displayLabel}
                          </Typography>
                        )}
                        <Typography variant="h6" component="h3" sx={{ fontWeight: 700 }}>
                          {getPlanTitle(plan)}
                        </Typography>
                      </Box>

                    <Box sx={{ minHeight: { md: compact ? '8.25rem' : '9rem' } }}>
                      {plan.originalAmountLabel && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ textDecoration: 'line-through', mb: 0.25 }}
                        >
                          {plan.originalAmountLabel}/{plan.intervalLabel}
                        </Typography>
                      )}
                      <Typography variant={compact ? 'h4' : 'h3'} sx={{ fontWeight: 800 }}>
                        {plan.amountLabel}
                        <Typography component="span" variant="body1" color="text.secondary">
                          /{plan.intervalLabel}
                        </Typography>
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {getTrialCopy(plan)}
                      </Typography>
                    </Box>

                    <Button
                      variant={isAnnual ? 'contained' : 'outlined'}
                      size="large"
                      fullWidth
                      onClick={() => handlePlanSelect(plan)}
                      startIcon={isCheckingOut ? <CircularProgress size={16} color="inherit" /> : null}
                      sx={{
                        mt: 'auto !important',
                        textTransform: 'none',
                        fontWeight: 700,
                        borderRadius: 2,
                        whiteSpace: 'nowrap',
                        pointerEvents: checkoutPriceId && !isCheckingOut ? 'none' : undefined,
                      }}
                    >
                      {isCheckingOut ? 'Starting checkout...' : plan.ctaLabel}
                    </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Box>
      )}

      {!loadingPricing && pricing?.plans.length > 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center' }}>
          Cancel anytime. Billing is managed securely by Stripe.
        </Typography>
      )}
    </Stack>
  );
}

export default MembershipPricingCards;
